"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var reactive_vue_1 = require("@formily/reactive-vue");
var vue_1 = require("@formily/vue");
var vue_demi_1 = require("vue-demi");
// @ts-ignore
var HotTable_vue_1 = __importDefault(require("./HotTable.vue"));
// @ts-ignore
var hotTableValidation_1 = require("./hotTableValidation");
// 设计器属性面板中，colHeaders / mergeCells 仍以 JSON 字符串形式存放在
// x-component-props 里（使用频率低、结构灵活，保留 JSON 高级录入方式）；
// columns 已改为可视化编辑器（ArrayItems + 列配置抽屉），落地后就是真正的
// 数组，不再需要 JSON.parse。
var JSON_PROP_KEYS = ['colHeaders', 'mergeCells'];
/**
 * 需要从字符串矫正为布尔值的属性。
 *
 * 移动端表单的 schema 经常由后端下发或人工编辑，布尔开关时常写成 "true" / "false"
 * 字符串。Vue 的 Boolean 类型 prop 拿到非空字符串 "false" 会被当成 true，
 * 表现为「明明配了不允许删除行，长按菜单里还是有删除」这类诡异现象，
 * 因此在连接器层统一矫正一次。
 */
var BOOLEAN_PROP_KEYS = [
    'readOnly',
    'allowInsertRow',
    'allowRemoveRow',
    'allowInsertColumn',
    'allowRemoveColumn',
    'showAddRowButton',
    'showErrorSummary',
    'filters',
    'columnSorting',
    'manualColumnResize',
    'manualRowResize',
    'manualColumnMove',
    'manualRowMove',
    'comments',
    'touchOptimized',
    'freezeFirstColumn',
    'tapToEdit',
    'tapEditDelay',
    'longPressRowMenu',
    'enableLandscape',
];
/**
 * 需要从字符串矫正为数字的属性。
 *
 * 与布尔同理，设计器的 NumberPicker 或后端下发的 schema 都可能给出字符串。
 * 其中 minSpareRows / minRows 尤其危险：handsontable 内核对它们用的是真值判断
 * （core.js 的 `if (priv.settings.minSpareRows)`），字符串 "0" 是 truthy，
 * 会让「不自动补空行」的配置彻底失效 —— 用户编辑着就凭空多一行、
 * 删掉最后一行又立刻被补回来。
 */
var NUMBER_PROP_KEYS = [
    'minSpareRows',
    'minRows',
    'minCols',
    'maxRows',
    'maxCols',
    'fixedColumnsLeft',
    'fixedRowsTop',
    'rowHeight',
    'minColWidth',
];
/** 字段处于这些形态时不做校验：详情查看 / 只读 / 禁用态不该被历史脏数据卡住 */
var NON_EDITABLE_PATTERNS = ['readOnly', 'readPretty', 'disabled'];
/** 避免同一个 field 被重复注入校验器的标记 */
var VALIDATOR_FLAG = '__hotTableValidatorAttached';
/**
 * Formily 内置的「标量格式」校验名（见 @formily/validator/formats）。
 * 这些规则本质是拿正则去匹配单个字符串值（日期、邮箱、数字……），
 * 对 HotTable 字段这种「值恒为数组」的场景没有意义，且永远不通过。
 */
var SCALAR_FORMAT_NAMES = [
    'date', 'dateTime', 'datetime', 'url', 'email', 'number', 'integer',
    'ipv6', 'ipv4', 'idcard', 'qq', 'phone', 'money', 'zh', 'zip',
];
var isScalarFormatName = function (name) { return typeof name === 'string' && SCALAR_FORMAT_NAMES.indexOf(name) !== -1; };
/**
 * 归一化 schema 上继承来的单条校验规则，剔除「对数组无意义的标量格式校验」：
 * - 函数、正则：原样保留（业务自定义规则）。
 * - 纯格式名字符串（如 'date'）：整条丢弃，返回 null。
 * - 规则对象：仅移除其 format 为标量格式的字段；若移除后还剩下 required / pattern /
 *   validator / len 等有效约束，则保留精简后的对象，否则整条丢弃。
 * 这样既避免了「该字段不是合法的日期格式」这类误报，又不会误伤 required、
 * 自定义函数等真正需要作用于整张表的校验。
 */
function sanitizeArrayIncompatibleRule(rule) {
    if (rule === null || rule === undefined)
        return null;
    if (typeof rule === 'function' || rule instanceof RegExp)
        return rule;
    if (typeof rule === 'string') {
        return isScalarFormatName(rule) ? null : rule;
    }
    if (typeof rule === 'object') {
        if (!isScalarFormatName(rule.format))
            return rule;
        var rest_1 = __assign({}, rule);
        delete rest_1.format;
        // 去掉 format 后仍带有其它有效约束才保留，只剩提示文案则视为空规则丢弃
        var meaningfulKeys = Object.keys(rest_1).filter(function (key) { return key !== 'message' && rest_1[key] !== undefined; });
        return meaningfulKeys.length ? rest_1 : null;
    }
    return rule;
}
function parseJsonAttrs(attrs) {
    var result = {};
    JSON_PROP_KEYS.forEach(function (key) {
        var raw = attrs[key];
        if (typeof raw !== 'string')
            return;
        if (!raw.trim()) {
            delete result[key];
            return;
        }
        try {
            result[key] = JSON.parse(raw);
        }
        catch (e) {
            // 配置的 JSON 不合法时忽略该项，交由 HotTable.vue 使用默认值，避免整表崩溃
            // eslint-disable-next-line no-console
            console.warn("[Formily HotTable] \u5C5E\u6027 ".concat(key, " \u4E0D\u662F\u5408\u6CD5 JSON\uFF0C\u5DF2\u5FFD\u7565\uFF1A"), raw);
        }
    });
    return result;
}
/** 把 "true" / "false" 这类字符串开关矫正成真正的布尔值 */
function parseBooleanAttrs(attrs) {
    var result = {};
    BOOLEAN_PROP_KEYS.forEach(function (key) {
        var raw = attrs[key];
        if (typeof raw !== 'string')
            return;
        var normalized = raw.trim().toLowerCase();
        if (normalized === 'true')
            result[key] = true;
        else if (normalized === 'false')
            result[key] = false;
    });
    return result;
}
/** 把 "0" / "10" 这类字符串数字矫正成真正的数字，非法值直接忽略走组件默认 */
function parseNumberAttrs(attrs) {
    var result = {};
    NUMBER_PROP_KEYS.forEach(function (key) {
        var raw = attrs[key];
        if (typeof raw !== 'string')
            return;
        var trimmed = raw.trim();
        if (!trimmed)
            return;
        var parsed = Number(trimmed);
        // 非数字（NaN / Infinity）时不写入，让组件用自己的默认值
        if (Number.isFinite(parsed))
            result[key] = parsed;
    });
    return result;
}
/**
 * 解析列的 className 配置，兼容「类名」与「直接写 CSS」两种写法。
 *
 * 与设计器侧 HotTablePreview/shared.ts 的 parseCellStyle 规则完全一致：
 * 按 `;` 切分，含 `:` 的片段视为 CSS 声明，其余视为类名，两者可混写。
 *   htRight                         —— 纯类名（原有写法，兼容）
 *   color: red; font-weight: bold   —— 纯 CSS 声明
 *   htCenter; color: #f60           —— 混写，类名与样式同时生效
 */
function parseCellStyle(raw) {
    if (typeof raw !== 'string' || !raw.trim())
        return { className: '', style: '' };
    var classNames = [];
    var declarations = [];
    raw
        .split(';')
        .map(function (seg) { return seg.trim(); })
        .filter(Boolean)
        .forEach(function (seg) {
        var colonIndex = seg.indexOf(':');
        if (colonIndex > 0) {
            var prop = seg.slice(0, colonIndex).trim();
            var value = seg.slice(colonIndex + 1).trim();
            // 属性名与值都要非空才算有效声明，避免 "a:" 这类残缺输入进入 cssText
            if (prop && value)
                declarations.push("".concat(prop, ": ").concat(value));
            return;
        }
        // 以 ':' 开头（如 ":red"）属于残缺声明，既不是合法类名也不是合法 CSS，丢弃
        if (colonIndex === 0)
            return;
        // 不含冒号：按空白再切一次，支持 "htRight htMiddle" 这种多类名写法
        seg.split(/\s+/).filter(Boolean).forEach(function (cls) { return classNames.push(cls); });
    });
    return {
        className: classNames.join(' '),
        style: declarations.length ? "".concat(declarations.join('; '), ";") : '',
    };
}
/**
 * 设计器"列配置"可视化编辑器里，source（可选项）/ numericFormat（数字格式）
 * 是给人填写的简化文本形式（逗号分隔字符串 / 格式串），这里转换成 handsontable
 * 真正需要的结构（数组 / { pattern } 对象），并按列类型清理掉不相关的字段。
 */
function normalizeColumns(columns) {
    if (!Array.isArray(columns))
        return columns;
    return columns
        .filter(function (col) { return col && typeof col === 'object'; })
        .map(function (col) {
        var result = __assign({}, col);
        if ((result.type === 'dropdown' || result.type === 'autocomplete')
            && typeof result.source === 'string') {
            result.source = result.source
                .split(',')
                .map(function (s) { return s.trim(); })
                .filter(Boolean);
        }
        else if (!Array.isArray(result.source)) {
            delete result.source;
        }
        if (result.type === 'numeric'
            && typeof result.numericFormat === 'string'
            && result.numericFormat.trim()) {
            result.numericFormat = { pattern: result.numericFormat.trim() };
        }
        else if (typeof result.numericFormat === 'string') {
            delete result.numericFormat;
        }
        if (result.type !== 'date') {
            delete result.dateFormat;
        }
        else if (typeof result.dateFormat !== 'string' || !result.dateFormat.trim()) {
            // 日期列未显式配置格式时兜底 YYYY/MM/DD：
            // handsontable 内核对日期单元格默认用 DD/MM/YYYY（见 vendor/handsontable/
            // defaultSettings.js），日期选择器填入、展示都会按该格式落值（如 10/09/2026），
            // 与后端/校验期望的年月日口径不一致、易误判为非法日期。这里统一兜底成
            // YYYY/MM/DD，日期选择器填入、单元格展示、格式校验三处保持一致。
            result.dateFormat = 'YYYY/MM/DD';
        }
        // className 可能混写了类名与 CSS 声明：类名走 handsontable 原生 className，
        // CSS 存到内部字段 __cellStyle，由 HotTable.vue 包装 renderer 时落到 td
        var parsedStyle = parseCellStyle(result.className);
        if (parsedStyle.className)
            result.className = parsedStyle.className;
        else
            delete result.className;
        if (parsedStyle.style)
            result.__cellStyle = parsedStyle.style;
        else
            delete result.__cellStyle;
        if (!result.width)
            delete result.width;
        if (!result.title)
            result.title = result.data;
        return result;
    });
}
// colHeaders 未显式填写 JSON 时，自动用各列的表头文字（title）兜底生成，
// 免去"列定义"和"列表头"重复填写。必填列在表头文字后补一个红色星号，
// 让用户录入前就知道哪几列不能空（handsontable 的 colHeaders 支持 HTML）。
function deriveColHeaders(columns) {
    if (!Array.isArray(columns) || !columns.length)
        return undefined;
    return columns.map(function (col) {
        var title = (col && (col.title || col.data)) || '';
        return col && col.required
            ? "".concat(title, "<span class=\"ht-required-mark\">*</span>")
            : title;
    });
}
/**
 * Formily x-component: 'HotTable'
 * 通用可编辑大表格（Handsontable）的移动端版本。
 *
 * 与 if-web 的同名连接器保持一致的对外契约：schema 里的 x-component-props
 * 与 HotTable.vue 的 props 一一对应，校验规则共用 ./hotTableValidation，
 * 因此同一份表单 schema 可以同时在 PC 端与移动端渲染。
 *
 * 移动端额外支持的 props（可在 x-component-props 里配）：
 *   touchOptimized      触摸优化总开关，默认 true
 *   freezeFirstColumn   冻结首列，默认 true
 *   tapToEdit           轻点编辑开关，默认 true
 *   tapEditDelay        轻点后延时多少毫秒唤起输入框，默认 1000；
 *                       延时窗口内拖动表格会取消唤起（0 = 立即唤起）
 *   longPressRowMenu    长按唤起行操作面板，默认 true
 *   enableLandscape     提供横屏编辑入口，默认 true
 *   rowHeight           行高，默认 40
 *   minColWidth         列最小宽度，默认 96
 *
 * 与 PC 端默认值不同、且不建议在移动端改动的项：
 *   stretchH      'none'（PC 为 'all'）。非 'none' 时内核会把整表宽度压回容器宽度，
 *                 宽表将无法左右滑动。
 *   minRows       0（PC 为 1）。非 0 时内核会自动补空行，导致「删除最后一行」
 *                 删完又被补回来。
 *   minSpareRows  0。非 0 时编辑最后一行会自动追加空行。
 *
 * 校验：这里给字段追加一个 validator，把表格内的非法数据（必填未填、值不在
 * 可选项内、数字/日期格式不对，规则见 ./hotTableValidation）汇总成错误信息
 * 抛给 Formily。这样 form.validate() 与提交前的字段校验会因该字段 invalid
 * 而失败，从源头阻止"带着非法数据提交"，而不只是把单元格标红。
 */
var formilyHotTable = (0, reactive_vue_1.observer)((0, vue_demi_1.defineComponent)({
    name: 'HotTable',
    props: ['value'],
    setup: function (curProps, _a) {
        var attrs = _a.attrs, emit = _a.emit;
        var fieldRef = (0, vue_1.useField)();
        /**
         * 字段校验器：每次校验时按"当前最新的列配置 + 当前值"重新计算，
         * 因此设计器里改完配置无需重建字段。
         */
        var validateValue = function (value) {
            var field = fieldRef && fieldRef.value;
            // 详情查看 / 只读 / 禁用态跳过校验，避免历史脏数据把页面卡死
            if (field && NON_EDITABLE_PATTERNS.indexOf(field.pattern) !== -1)
                return '';
            var columns = normalizeColumns(attrs.columns);
            if (!Array.isArray(columns) || !columns.length)
                return '';
            var rows = Array.isArray(value) ? value : [];
            // 空表格（0 行）不做任何列校验：移动端维持 minRows=0，空表就是 0 行的
            // 未录入态（不像 PC 端有 minRows=1 的内核自动补行），没有行时列的必填/
            // 格式校验没有作用对象；用户点了「添加一行」、表里出现数据行后才生效。
            if (!rows.length)
                return '';
            // 整表全是用户加的空行时，行级必填校验会把每个空行都报一遍"不能为空"，
            // 堆成一长串。这里兜底：存在必填列但用户一个字段都没填时，只按第一处
            // 必填给出一条提示（与 PC 端一致）。
            var hasRequiredColumn = columns.some(function (col) { return col && col.required === true; });
            if (hasRequiredColumn && !rows.some(function (row) { return !(0, hotTableValidation_1.isRowEmpty)(row, columns); })) {
                var firstRequired = columns.find(function (col) { return col && col.required === true; });
                var title = (firstRequired && (firstRequired.title || firstRequired.data)) || '表格';
                return "\u8BF7\u586B\u5199\u8868\u683C\u5185\u5BB9\uFF08\u7B2C1\u884C\u300C".concat(title, "\u300D\u4E0D\u80FD\u4E3A\u7A7A\uFF09");
            }
            var errors = (0, hotTableValidation_1.validateTableData)(rows, columns);
            return (0, hotTableValidation_1.formatValidationMessage)(errors);
        };
        var field = fieldRef && fieldRef.value;
        if (field && typeof field.setValidator === 'function' && !field[VALIDATOR_FLAG]) {
            field[VALIDATOR_FLAG] = true;
            // 追加而非覆盖：schema 上通过 x-validator 配置的规则要继续生效。
            // 但要先剔除「对数组值无意义的标量格式校验」（date/email/number 等）：
            // HotTable 字段的值恒为整张表的数组，这类格式规则跑在数组上永远不通过，
            // 会恒定抛出诸如「该字段不是合法的日期格式」的误报（本应校验的是某个日期
            // 单元格，规则却被挂到了整张表字段上）。列内的日期/数字等格式由我们自己的
            // 单元格校验（validateTableData）负责。
            var existing = field.validator;
            var rawRules = Array.isArray(existing)
                ? existing.slice()
                : existing
                    ? [existing]
                    : [];
            var rules = rawRules
                .map(sanitizeArrayIncompatibleRule)
                .filter(function (rule) { return rule !== null && rule !== undefined; });
            rules.push(function (value) { return validateValue(value); });
            field.setValidator(rules);
        }
        return function () {
            var _a, _b;
            var columns = normalizeColumns(attrs.columns);
            var jsonAttrs = parseJsonAttrs(attrs);
            var booleanAttrs = parseBooleanAttrs(attrs);
            var numberAttrs = parseNumberAttrs(attrs);
            var colHeaders = jsonAttrs.colHeaders !== undefined
                ? jsonAttrs.colHeaders
                : deriveColHeaders(columns);
            return (0, vue_1.h)(HotTable_vue_1.default, {
                attrs: __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, attrs), curProps), jsonAttrs), booleanAttrs), numberAttrs), { columns: columns }), (colHeaders !== undefined ? { colHeaders: colHeaders } : {})), { value: (_b = (_a = curProps.value) !== null && _a !== void 0 ? _a : attrs.value) !== null && _b !== void 0 ? _b : [] }),
                on: {
                    input: function (val) {
                        emit('change', val);
                    },
                    change: function (payload) {
                        emit('tableChange', payload);
                    },
                    selection: function (payload) {
                        emit('selectionChange', payload);
                    },
                    // 表格内校验结果变化时透出，方便业务方自定义提示
                    validate: function (errors) {
                        emit('validateChange', errors);
                    },
                    // 横屏 / 全屏状态变化透出：外层页面可以据此隐藏自己的固定底栏，
                    // 否则底部提交条会浮在全屏表格之上
                    'fullscreen-change': function (fullscreen) {
                        emit('fullscreenChange', fullscreen);
                    },
                },
            }, {});
        };
    },
}));
exports.default = formilyHotTable;
